import React, { useState } from 'react';
import { NavLink, useNavigate, useLocation } from 'react-router-dom';
import { useAuth, Capability } from '../context/AuthContext';
import { 
  LayoutDashboard, 
  Users, 
  FileText, 
  Building2, 
  Receipt, 
  CreditCard, 
  AlertTriangle, 
  CheckSquare,
  Wallet,
  LogOut,
  Hammer,
  Menu,
  Calculator,
  Settings,
  Calendar,
  HelpCircle,
  Home,
  ClipboardList,
  X,
  ChevronLeft,
  UserCog,
  Shield
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { cn } from '../lib/utils';
import TrialBanner from './TrialBanner';
import MandoIA from './MandoIA';

const navItems = [
  { path: '/', icon: LayoutDashboard, label: 'Dashboard' },
  { path: '/clientes', icon: Users, label: 'Clientes' },
  { path: '/presupuestos', icon: FileText, label: 'Presupuestos' },
  { path: '/obras', icon: Building2, label: 'Obras' },
  { path: '/calendario', icon: Calendar, label: 'Calendario' },
  { path: '/gastos', icon: Wallet, label: 'Gastos' },
  { path: '/facturas', icon: Receipt, label: 'Facturas' },
  { path: '/pagos', icon: CreditCard, label: 'Pagos' },
  { path: '/incidencias', icon: AlertTriangle, label: 'Incidencias' },
  { path: '/pendientes', icon: CheckSquare, label: 'Pendientes' },
  { path: '/calculadora', icon: Calculator, label: 'Calculadora ROI' },
  { path: '/gestion-usuarios', icon: UserCog, label: 'Usuarios', capability: Capability.MANAGE_USERS },
  { path: '/configuracion-empresa', icon: Settings, label: 'Config. Empresa', capability: Capability.MANAGE_COMPANY },
  { path: '/ayuda', icon: HelpCircle, label: 'Ayuda' },
];

// Bottom navigation items for mobile
const bottomNavItems = [
  { path: '/', icon: Home, label: 'Inicio' },
  { path: '/obras', icon: Building2, label: 'Obras' },
  { path: '/pendientes', icon: ClipboardList, label: 'Tareas' },
];

// Get page title from path
const getPageTitle = (pathname, companyName) => {
  const item = navItems.find(i => i.path === pathname);
  return item?.label || companyName;
};

const Layout = ({ children }) => {
  const { user, companyName, isDemo, isSuperadmin, can, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [menuOpen, setMenuOpen] = useState(false);

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  const isHome = location.pathname === '/';
  const pageTitle = getPageTitle(location.pathname, companyName);

  // Display name - use company name or default
  const displayName = companyName || 'Mi Empresa';

  return (
    <div className="min-h-screen bg-slate-50" data-testid="main-layout">
      {/* ==================== MOBILE HEADER ==================== */}
      <header className="lg:hidden fixed top-0 left-0 right-0 h-14 bg-white/95 backdrop-blur-md border-b border-slate-200 z-50 flex items-center justify-between px-4 shadow-sm">
        {/* Left side - Back or Logo */}
        {isHome ? (
          <div className="flex items-center gap-2">
            <div className="w-8 h-8 bg-orange-500 rounded-lg flex items-center justify-center">
              <Hammer className="w-4 h-4 text-white" />
            </div>
            <div className="flex flex-col">
              <span className="text-sm font-bold text-slate-900 leading-tight truncate max-w-[120px]">{displayName}</span>
              <span className="text-[9px] text-slate-400 leading-tight">{isDemo ? 'DEMO' : 'ERP'}</span>
            </div>
          </div>
        ) : (
          <button 
            onClick={() => navigate(-1)}
            className="flex items-center gap-1 text-slate-600 active:scale-95 transition-transform"
            data-testid="mobile-back-btn"
          >
            <ChevronLeft className="w-5 h-5" />
            <span className="text-sm font-medium">Atrás</span>
          </button>
        )}

        {/* Center - Page Title */}
        <h1 className="absolute left-1/2 -translate-x-1/2 text-base font-bold text-slate-900">
          {isHome ? '' : pageTitle}
        </h1>

        {/* Right side - Menu */}
        <Button
          variant="ghost"
          size="icon"
          className="h-10 w-10 text-slate-700 hover:bg-slate-100 active:scale-95 transition-all"
          onClick={() => setMenuOpen(true)}
          data-testid="mobile-menu-btn"
        >
          <Menu className="w-5 h-5" />
        </Button>
      </header>

      {/* ==================== MOBILE FULL MENU ==================== */}
      <div 
        className={cn(
          "lg:hidden fixed inset-0 bg-slate-900 z-[60] transition-transform duration-300 ease-out",
          menuOpen ? "translate-x-0" : "translate-x-full"
        )}
      >
        {/* Menu Header */}
        <div className="flex items-center justify-between p-4 border-b border-slate-800">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-slate-700 rounded-full flex items-center justify-center">
              <span className="text-sm font-bold text-white">
                {user?.nombre?.charAt(0)?.toUpperCase() || 'U'}
              </span>
            </div>
            <div>
              <p className="text-sm font-semibold text-white">{user?.nombre || 'Usuario'}</p>
              <p className="text-xs text-slate-400">{user?.email}</p>
            </div>
          </div>
          <Button
            variant="ghost"
            size="icon"
            className="h-10 w-10 text-slate-400 hover:text-white"
            onClick={() => setMenuOpen(false)}
            data-testid="close-menu-btn"
          >
            <X className="w-6 h-6" />
          </Button>
        </div>

        {/* Menu Navigation */}
        <nav className="p-4 space-y-1 overflow-y-auto max-h-[calc(100vh-180px)]">
          {navItems
            .filter(item => !item.capability || can(item.capability))
            .map((item) => (
            <NavLink
              key={item.path}
              to={item.path}
              onClick={() => setMenuOpen(false)}
              className={({ isActive }) =>
                cn(
                  "flex items-center gap-4 px-4 py-3.5 rounded-xl text-base font-medium transition-all duration-200 active:scale-[0.98]",
                  isActive || (item.path === '/' && location.pathname === '/')
                    ? "bg-orange-500 text-white"
                    : "text-slate-300 hover:bg-slate-800"
                )
              }
              data-testid={`menu-${item.path.replace('/', '') || 'dashboard'}`}
            >
              <item.icon className="w-5 h-5" />
              <span>{item.label}</span>
            </NavLink>
          ))}
          
          {/* Superadmin link */}
          {isSuperadmin && (
            <NavLink
              to="/superadmin"
              onClick={() => setMenuOpen(false)}
              className={({ isActive }) =>
                cn(
                  "flex items-center gap-4 px-4 py-3.5 rounded-xl text-base font-medium transition-all duration-200 mt-4 border-t border-slate-800 pt-4",
                  isActive ? "bg-purple-500 text-white" : "text-purple-400 hover:bg-slate-800"
                )
              }
              data-testid="menu-superadmin"
            >
              <Shield className="w-5 h-5" />
              <span>Superadmin</span>
            </NavLink>
          )}
        </nav>

        {/* Menu Footer */}
        <div className="absolute bottom-0 left-0 right-0 p-4 border-t border-slate-800 bg-slate-900">
          <Button
            variant="ghost"
            className="w-full h-12 justify-start gap-3 text-slate-400 hover:text-white hover:bg-slate-800 rounded-xl text-base"
            onClick={() => { handleLogout(); setMenuOpen(false); }}
            data-testid="mobile-logout-btn"
          >
            <LogOut className="w-5 h-5" />
            Cerrar Sesión
          </Button>
          <p className="text-[10px] text-slate-600 text-center mt-3">MANDO · by Leapifyes</p>
        </div>
      </div>

      {/* ==================== DESKTOP SIDEBAR ==================== */}
      <aside 
        className="hidden lg:block fixed left-0 top-0 h-full w-64 bg-slate-900 z-40"
        data-testid="sidebar"
      >
        <div className="flex flex-col h-full">
          {/* Logo */}
          <div className="h-20 flex flex-col justify-center px-6 border-b border-slate-800">
            <div className="flex items-center gap-3">
              <div className="w-9 h-9 bg-orange-500 rounded-md flex items-center justify-center">
                <Hammer className="w-5 h-5 text-white" />
              </div>
              <span className="text-xl font-bold text-white tracking-tight truncate max-w-[150px]" title={displayName}>
                {displayName}
              </span>
            </div>
            <p className="text-[10px] text-slate-500 mt-1 pl-12">
              {isDemo ? 'DEMO · ' : ''}MANDO by Leapifyes
            </p>
          </div>

          {/* Navigation */}
          <nav className="flex-1 py-4 px-3 space-y-1 overflow-y-auto">
            {navItems
              .filter(item => !item.capability || can(item.capability))
              .map((item) => (
              <NavLink
                key={item.path}
                to={item.path}
                className={({ isActive }) =>
                  cn(
                    "flex items-center gap-3 px-4 py-2.5 rounded-md text-sm font-medium transition-colors duration-200",
                    isActive || (item.path === '/' && location.pathname === '/')
                      ? "bg-slate-800 text-white"
                      : "text-slate-400 hover:bg-slate-800 hover:text-white"
                  )
                }
                data-testid={`nav-${item.path.replace('/', '') || 'dashboard'}`}
              >
                <item.icon className="w-5 h-5" />
                <span>{item.label}</span>
              </NavLink>
            ))}
            
            {/* Superadmin link */}
            {isSuperadmin && (
              <NavLink
                to="/superadmin"
                className={({ isActive }) =>
                  cn(
                    "flex items-center gap-3 px-4 py-2.5 rounded-md text-sm font-medium transition-colors duration-200 mt-4 border-t border-slate-800 pt-4",
                    isActive ? "bg-purple-600 text-white" : "text-purple-400 hover:bg-slate-800 hover:text-purple-300"
                  )
                }
                data-testid="nav-superadmin"
              >
                <Shield className="w-5 h-5" />
                <span>Superadmin</span>
              </NavLink>
            )}
          </nav>

          {/* User section */}
          <div className="p-4 border-t border-slate-800">
            <div className="flex items-center gap-3 mb-3">
              <div className="w-9 h-9 bg-slate-700 rounded-full flex items-center justify-center">
                <span className="text-sm font-medium text-white">
                  {user?.nombre?.charAt(0)?.toUpperCase() || 'U'}
                </span>
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-sm font-medium text-white truncate">{user?.nombre || 'Usuario'}</p>
                <p className="text-xs text-slate-400 truncate">{user?.email}</p>
              </div>
            </div>
            <Button
              variant="ghost"
              className="w-full justify-start text-slate-400 hover:text-white hover:bg-slate-800"
              onClick={handleLogout}
              data-testid="logout-btn"
            >
              <LogOut className="w-4 h-4 mr-2" />
              Cerrar Sesión
            </Button>
          </div>
        </div>
      </aside>

      {/* ==================== BOTTOM NAVIGATION (Mobile) ==================== */}
      <nav className="lg:hidden fixed bottom-0 left-0 right-0 h-16 bg-slate-900 border-t border-slate-800 z-50 flex items-center justify-around pb-safe" data-testid="bottom-nav">
        {bottomNavItems.map((item) => {
          const isActive = location.pathname === item.path;
          return (
            <NavLink
              key={item.path}
              to={item.path}
              className="flex flex-col items-center justify-center w-16 h-full gap-0.5 active:scale-90 transition-transform"
              data-testid={`bottom-nav-${item.path.replace('/', '') || 'home'}`}
            >
              <item.icon className={cn(
                "w-6 h-6 transition-colors",
                isActive ? "text-orange-500" : "text-slate-400"
              )} />
              <span className={cn(
                "text-[10px] font-semibold transition-colors",
                isActive ? "text-orange-500" : "text-slate-500"
              )}>
                {item.label}
              </span>
            </NavLink>
          );
        })}
        
        {/* Menu button in bottom nav */}
        <button
          onClick={() => setMenuOpen(true)}
          className="flex flex-col items-center justify-center w-16 h-full gap-0.5 active:scale-90 transition-transform"
          data-testid="bottom-nav-menu"
        >
          <Menu className="w-6 h-6 text-slate-400" />
          <span className="text-[10px] font-semibold text-slate-500">Menú</span>
        </button>
      </nav>

      {/* ==================== MAIN CONTENT ==================== */}
      <main className="lg:ml-64 min-h-screen pt-14 pb-20 lg:pt-0 lg:pb-0">
        {/* Trial Banner */}
        <div className="hidden lg:block">
          <TrialBanner />
        </div>
        <div className="lg:hidden fixed top-14 left-0 right-0 z-40">
          <TrialBanner />
        </div>
        <div className="p-4 lg:p-8">
          {children}
        </div>
      </main>

      {/* MANDO IA - Intelligent Assistant */}
      <MandoIA />
    </div>
  );
};

export default Layout;
